import { ChangeDetectorRef, Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import type { NgxForm } from '../../shared/components/form/ngx-form.types';
import { FormComponent } from '../../shared/components/form/form.component';
import { OverlayService } from '../../shared/services/overlay.service';
import { RagDatasupplyFrontendService } from '@reflact/rag-datasupply';
import { ToastrService } from 'ngx-toastr';
import { MandantService } from 'src/app/shared/services/mandant.service';
import { TileService, TileIndividualAccessGrant } from 'src/app/shared/services/tile.service';
import { Tile } from 'src/app/shared/types/tile.types';
import { Mandant } from 'src/app/shared/types/mandant.types';
import { WebTileEditorComponent } from 'src/app/shared/components/web-tile-editor/web-tile-editor.component';
import { TileImageService } from 'src/app/shared/services/tile-image.service';

@Component({
  templateUrl: './tile-administration.component.html',
  styleUrls: ['./tile-administration.component.scss'],
  providers: [RagDatasupplyFrontendService],
  standalone: false
})
export class TileAdministrationComponent implements OnInit, OnDestroy {

  /** Zeilenmarkierung in der p-table — unabhängig vom Bearbeiten-Drawer, siehe closeTileDetail(). */
  public selectedTile?: Tile;
  /** Steuert den Bearbeiten-Drawer (siehe selectTileForEdit()/closeTileDetail()). */
  public editVisible = false;
  public editTile?: Tile;
  /** Zugriff auf die Save/Delete-Actions des Formulars — die Buttons sitzen im Drawer-Footer
   * (showActions=false) statt im mitscrollenden Formularinhalt, siehe app-form#showActions. */
  @ViewChild('tileForm') public tileFormRef?: FormComponent;
  /** Steuert das kurze visuelle Feedback am Speichern-Button (statt den Drawer zu schließen),
   * siehe updateTile(). */
  public tileSaveFeedback: 'idle' | 'saved' = 'idle';
  public tileSaving = false;
  @ViewChild('webTileEditor') public webTileEditor?: WebTileEditorComponent;
  public translatedLabels: NgxForm.TranslatedLabels = new Map<string, string>([
    ["name", $localize`:@@globalName:Name`],
    ["openInNewTab", $localize`:@@tilesAdminOpenInNewTab:Öffnen in neuem Tab`],
    ["subtext", $localize`:@@webTileDescription:Beschreibung`],
    ["url", $localize`:@@tilesAdminURL:URL`],
    ["kind", $localize`:@@tilesAdminKind:Verknüpfungstyp`],
  ])
  /** Tabs im Bearbeiten-Drawer (mit Info-Tab) bzw. Erstellen-Drawer (ohne, da eine neue Kachel
   * noch keine Mandanten-/Personen-Zuweisungen haben kann), siehe app-form#tabs. */
  public readonly tileEditFormTabs = [
    { id: 'appearance', label: $localize`:@@tilesAdminTabAppearance:Erscheinungsbild` },
    { id: 'settings', label: $localize`:@@tilesAdminSectionOptions:Einstellungen` },
    { id: 'info', label: $localize`:@@tilesAdminTabInfo:Info` },
  ];
  public iconPickerVisible = false;
  public iconPickerTile?: Tile;
  /** Kompakte Typ-Badge für die Tabelle — anders formuliert als `tileKinds` (das ist für den
   * Verknüpfungstyp-Dropdown beim Anlegen gedacht, hier geht es nur um die Anzeige). */
  public readonly tileKindBadgeLabels: Record<'link' | 'application', string> = {
    link: $localize`:@@webTileKind:Weblink`,
    application: $localize`:@@tilesAdminKindBadgeApplication:Application (Logto)`,
  };
  /** Für die Spalte/den Dialog "Mandanten mit Zugriff" — wer eine Kachel im Pool (`mandant.tiles`)
   * hat, siehe getMandantsForTile(). Einmal geladen, wie `applicationOptions`; wird nach
   * Aktionen aktualisiert, die Mandanten-Zuweisungen ändern (löschen/verknüpfen). */
  public mandants: Mandant[] = [];
  /** Personen mit individueller Freigabe für die gerade bearbeitete Kachel, mandantenweit —
   * siehe loadIndividualAccess(), gezeigt im Info-Tab (mandantAssignmentsTpl daneben). */
  public individualAccess: TileIndividualAccessGrant[] = [];
  public loadingIndividualAccess = false;
  /** Superadmin-Dialog "Fehlende Bilder generieren" — nur sichtbar, wenn fal.ai konfiguriert ist. */
  public imageBackfillVisible = false;
  public imageGenerationAvailable = false;
  constructor(public frontendService: RagDatasupplyFrontendService<Tile>, private mandantService: MandantService, private tileService: TileService, private toastrService: ToastrService, private overlayService: OverlayService, private cdr: ChangeDetectorRef, private tileImageService: TileImageService) {
    this.tileService.listTiles().then(m => this.frontendService.fromArray(m))
    this.loadMandants();
    this.tileImageService.capabilities().then(capabilities => {
      this.imageGenerationAvailable = capabilities.available;
      if (!this.destroyed) this.cdr.detectChanges();
    }).catch(() => { });
  }

  private async loadMandants(): Promise<void> {
    try {
      this.mandants = await this.mandantService.getMandants();
    } finally {
      if (!this.destroyed) this.cdr.detectChanges();
    }
  }

  private destroyed = false;

  ngOnDestroy(): void {
    this.destroyed = true;
  }

  ngOnInit(): void {
  }

  public async onWebTileSaved(): Promise<void> {
    this.frontendService.fromArray(await this.tileService.listTiles());
    await this.loadMandants();
  }

  public async updateTile(data: Tile) {
    this.tileSaving = true;
    this.cdr.detectChanges();
    let res;
    try {
      res = await this.tileService.editTile(data);
    } finally {
      this.tileSaving = false;
      this.cdr.detectChanges();
    }
    if (res.status == "ok") {
      this.editTileSnapshot = JSON.stringify(data);
      this.flashTileSaveFeedback();
    }
    this.tileService.listTiles().then(m => this.frontendService.fromArray(m))
  }

  /** Vergleicht `editTile` mit dem beim Öffnen/letzten Speichern erfassten Stand — der
   * Speichern-Button soll erst aktiv werden, wenn tatsächlich etwas geändert wurde. */
  private editTileSnapshot?: string;

  public hasTileChanges(): boolean {
    return !!this.editTile && JSON.stringify(this.editTile) !== this.editTileSnapshot;
  }

  /** Kurzes Erfolgs-Feedback direkt am Speichern-Button, statt den Drawer zu schließen — so kann
   * direkt weiter an derselben Kachel gearbeitet werden. */
  private flashTileSaveFeedback(): void {
    this.tileSaveFeedback = 'saved';
    this.cdr.detectChanges();
    setTimeout(() => {
      this.tileSaveFeedback = 'idle';
      this.cdr.detectChanges();
    }, 1500);
  }

  /** Bestätigungsdialog fürs endgültige Löschen — zum Bestätigen muss der Kachelname eingetippt
   * werden, siehe canConfirmDelete(). */
  public deleteDialogVisible = false;
  public deleteConfirmName = '';
  public deleting = false;

  public openDeleteDialog(): void {
    this.deleteConfirmName = '';
    this.deleteDialogVisible = true;
  }

  public canConfirmDelete(tile: Tile): boolean {
    return !this.deleting && this.deleteConfirmName.trim() === tile.name.trim();
  }

  public async confirmDeleteTile(tile: Tile): Promise<void> {
    if (!this.canConfirmDelete(tile)) return;
    this.deleting = true;
    try {
      await this.deleteTile(tile);
    } finally {
      this.deleting = false;
      this.deleteDialogVisible = false;
      this.cdr.detectChanges();
    }
  }

  public async deleteTile(data: Tile) {
    const res = await this.tileService.deleteTile(data._id)
    if (res.status == "ok") {
      this.toastrService.success($localize`:@@tilesAdminDeleted:Kachel gelöscht.`);
      await this.closeTileDetail();
      this.cdr.detectChanges();
      this.loadMandants();
    } else {
      this.toastrService.error(res.message ?? $localize`:@@tilesAdminDeleteFailed:Diese Kachel kann nicht gelöscht werden.`);
    }
    this.tileService.listTiles().then(m => this.frontendService.fromArray(m))

  }

  /** Öffnet die RemixIcon-Auswahl (Klick auf das Icon in der Kachel-Vorschau). */
  public openIconPicker(tile?: Tile) {
    if (!tile) return;
    this.iconPickerTile = tile;
    this.iconPickerVisible = true;
  }

  /** Übernimmt den im Dialog gewählten RemixIcon-Klassennamen ins Tile. */
  public onIconPickerAccepted(icon: string) {
    if (this.iconPickerTile) {
      this.iconPickerTile.icon = icon;
      const tile = this.iconPickerTile;
      tile.coverImageUrl ??= tile.imageMode === 'cover' ? (tile.imageUrl ?? '') : '';
      tile.iconImageUrl = '';
      tile.iconImagePrompt = '';
      tile.imageUrl = '';
    }
    this.cdr.detectChanges();
  }

  public getKindBadgeLabel(tile: Tile): string {
    return this.tileKindBadgeLabels[tile.kind] ?? tile.kind;
  }

  /** Mandanten, die diese Kachel in ihrem Pool (`mandant.tiles`) haben — mandant.tiles ist
   * historisch als ObjectId[] typisiert, tatsächlich sind Kachel-Ids seit dem Logto-Umstieg
   * einfache Strings (siehe mandant.types.ts). */
  public getMandantsForTile(tile: Tile): Mandant[] {
    return this.mandants.filter(m => (m.tiles as unknown as string[]).includes(tile._id));
  }

  public getMandantCount(tile: Tile): number {
    return this.getMandantsForTile(tile).length;
  }

  /** Ob der Mandanten-Admin diese Kachel für alle Nutzer seines Mandanten aktiviert hat
   * (`mandant.activeTiles`) — Gegenstück zu bloß im Pool (`mandant.tiles`) zu sein, siehe
   * mandantAssignmentsTpl im Info-Tab. */
  public isTileActiveForMandant(tile: Tile, mandant: Mandant): boolean {
    return (mandant.activeTiles as unknown as string[]).includes(tile._id);
  }

  /** Anzahl individueller Freigaben (`mandant.userTiles`) für diese Kachel in genau diesem
   * Mandanten — auch relevant, wenn die Kachel dort NICHT global aktiviert ist (siehe
   * mandantAssignmentsTpl im Info-Tab: "Nicht aktiviert" wäre sonst irreführend, solange
   * einzelne Personen die Kachel trotzdem individuell zugewiesen bekommen haben). */
  public getIndividualAccessCountForMandant(mandant: Mandant): number {
    return this.individualAccess.filter(grant => grant.mandantId === mandant._id.toString()).length;
  }

  /** Tooltip-Text für das Mandanten-Badge in der Tabelle. */
  public getMandantNamesTitle(tile: Tile): string {
    const names = this.getMandantsForTile(tile).map(m => m.name);
    return names.length > 0 ? names.join(', ') : $localize`:@@tilesAdminMandantsNone:In keinem Kachel-Pool enthalten.`;
  }

  public selectTileForEdit(tile: Tile) {
    if (tile.kind === 'link') {
      this.webTileEditor?.open(tile);
      return;
    }
    this.editTile = { ...tile, customPermissions: (tile.customPermissions ?? []).map(cp => ({ ...cp })) };
    this.editTileSnapshot = JSON.stringify(this.editTile);
    this.editVisible = true;
    this.tileSaveFeedback = 'idle';
    this.overlayService.set('selected', tile._id.toString());
    this.loadIndividualAccess(tile._id);
  }

  /** Schließt den Bearbeiten-Drawer (Abbruch, Löschen oder Deep-Link ohne Treffer). Awaited von
   * den Aufrufern, bevor die Kachelliste neu geladen wird - sonst kann `results$` erneut emittieren,
   * während der `selected`-Query-Param in der URL noch auf die alte Kachel zeigt (die Navigation
   * dafür ist async), und die Deep-Link-Subscription in ngAfterViewInit() öffnet den Drawer sofort
   * wieder. */
  public async closeTileDetail(): Promise<void> {
    this.editVisible = false;
    this.editTile = undefined;
    this.selectedTile = undefined;
    this.individualAccess = [];
    this.tileSaveFeedback = 'idle';
    await this.overlayService.set('selected', undefined);
  }


  /** Spaltenbeschriftung der p-table — nutzt dieselben Übersetzungen wie das Bearbeiten-Formular. */
  public getColumnLabel(fieldName: string): string {
    return this.translatedLabels.get(fieldName) ?? fieldName;
  }

  ngAfterViewInit(): void {
    this.frontendService.results$.subscribe(r => {
      if (r && r.length > 0 && this.overlayService.get("selected")) {
        const found = r.find(u => u._id.toString() == this.overlayService.get("selected"));
        if (found == undefined) {
          void this.closeTileDetail();
          return;
        }
        if (found.kind === 'link') {
          void this.overlayService.set('selected', undefined);
          this.webTileEditor?.open(found);
          return;
        }
        // Drawer für diese Kachel bereits offen (z.B. Refetch nach Speichern) — nicht mit einem
        // frisch nachgeladenen Stand überschreiben, sonst kann die gerade gespeicherte Zuweisung
        // durch noch nicht propagierte Serverdaten wieder zurückspringen.
        if (this.editVisible && this.editTile?._id === found._id) {
          return;
        }
        this.editTile = { ...found, customPermissions: (found.customPermissions ?? []).map(cp => ({ ...cp })) };
        this.editTileSnapshot = JSON.stringify(this.editTile);
        this.editVisible = true;
        this.loadIndividualAccess(found._id);
      }
    })
  }

  /** Lädt die Personen mit individueller Freigabe für die gerade geöffnete Kachel (Info-Tab). */
  private async loadIndividualAccess(tileId: string): Promise<void> {
    this.loadingIndividualAccess = true;
    this.individualAccess = await this.tileService.listIndividualAccess(tileId);
    this.loadingIndividualAccess = false;
    this.cdr.detectChanges();
  }

  public open() {
    this.webTileEditor?.open();
  }

  /** Eingabefelder für einen neuen Katalog-Eintrag im "Zusätzliche Rechte"-Editor (Settings-Tab). */
  public newCustomPermissionKey = '';
  public newCustomPermissionLabel = '';

  public addCustomPermission(): void {
    const key = this.newCustomPermissionKey.trim();
    const label = this.newCustomPermissionLabel.trim();
    if (!this.editTile || !key || !label) return;
    if (this.editTile.customPermissions?.some(cp => cp.key === key)) return;
    this.editTile.customPermissions = [...(this.editTile.customPermissions ?? []), { key, label }];
    this.newCustomPermissionKey = '';
    this.newCustomPermissionLabel = '';
  }

  public removeCustomPermission(key: string): void {
    if (!this.editTile) return;
    this.editTile.customPermissions = (this.editTile.customPermissions ?? []).filter(cp => cp.key !== key);
  }

}
